import { formatBirthMonthYear } from '@/shared/utils/date';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { CatSubscription } from '../types';

dayjs.extend(utc);

const formatYearAgeLabel = (years: number): string => (years === 1 ? '1 year' : `${years} years`);

const formatMonthAgeLabel = (months: number): string =>
  months === 1 ? '1 month' : `${months} months`;

const getAgeInMonthsFromBirthdate = (birthdate: string): number | null => {
  const birth = dayjs.utc(birthdate).startOf('month');
  const now = dayjs.utc().startOf('month');

  if (!birth.isValid() || birth.isAfter(now)) {
    return null;
  }

  return now.diff(birth, 'month');
};

const formatCurrentAgeForDobDisplay = (birthdate: string | null): string => {
  if (!birthdate) {
    return '-';
  }

  const ageInMonths = getAgeInMonthsFromBirthdate(birthdate);

  if (ageInMonths === null || ageInMonths < 0) {
    return '-';
  }

  if (ageInMonths < 12) {
    return formatMonthAgeLabel(ageInMonths);
  }

  const years = Math.floor(ageInMonths / 12);
  const months = ageInMonths % 12;

  if (months === 0) {
    return formatYearAgeLabel(years);
  }

  return `${formatYearAgeLabel(years)}, ${formatMonthAgeLabel(months)}`;
};

/**
 * Format estimated DOB and age for admin display.
 */
export const formatEstimatedDobAndAge = (catSubscription: CatSubscription): string => {
  if (!catSubscription.birthdate) {
    return '-';
  }

  const age = formatCurrentAgeForDobDisplay(catSubscription.birthdate);

  if (age === '-') {
    return '-';
  }

  return `${formatBirthMonthYear(catSubscription.birthdate)} (${age})`;
};

/**
 * Format age with unit into readable string
 */
export const formatAge = (age?: string, unit?: string): string => {
  if (!age) return '-';
  if (!unit) return age;
  const ageNum = parseInt(age, 10);
  if (isNaN(ageNum)) return `${age} ${unit}`;
  return `${age} ${unit}${ageNum !== 1 ? 's' : ''}`;
};

/**
 * Format dislikes array into readable string
 */
export const formatDislikes = (dislikes?: string[]): string => {
  if (!dislikes || dislikes.length === 0) return 'None';
  return dislikes.join(', ');
};

/**
 * Format meal type into readable string
 */
export const formatMealType = (mealType?: string): string => {
  if (!mealType) return '-';
  return mealType.charAt(0).toUpperCase() + mealType.slice(1);
};

/**
 * Format meal size into readable string
 */
export const formatMealSize = (mealSize?: string): string => {
  if (!mealSize) return '-';
  return mealSize;
};

/**
 * Format colour into readable string
 */
export const formatColour = (colour: CatSubscription['colour']): string => {
  if (!colour) return '-';
  return colour.charAt(0).toUpperCase() + colour.slice(1);
};

/**
 * Format funnel data into readable format
 */
export const formatFunnelData = (
  funnelData: CatSubscription['funnelData'],
): Array<{ label: string; value: string }> => {
  if (!funnelData) return [];

  const formatted: Array<{ label: string; value: string }> = [];

  if (funnelData.age || funnelData.unit) {
    formatted.push({
      label: 'Age',
      value: formatAge(funnelData.age, funnelData.unit),
    });
  }

  if (funnelData.dislikes && funnelData.dislikes.length > 0) {
    formatted.push({
      label: 'Dislikes',
      value: formatDislikes(funnelData.dislikes),
    });
  }

  if (funnelData.mealSize) {
    formatted.push({
      label: 'Meal Size',
      value: formatMealSize(funnelData.mealSize),
    });
  }

  if (funnelData.mealType) {
    formatted.push({
      label: 'Meal Type',
      value: formatMealType(funnelData.mealType),
    });
  }

  return formatted;
};

/**
 * Format funnel data into a single readable string for table display
 */
export const formatFunnelDataInline = (funnelData: CatSubscription['funnelData']): string => {
  if (!funnelData) return '-';

  const parts: string[] = [];

  if (funnelData.age || funnelData.unit) {
    parts.push(`Age: ${formatAge(funnelData.age, funnelData.unit)}`);
  }

  if (funnelData.mealType) {
    parts.push(`Meal Type: ${formatMealType(funnelData.mealType)}`);
  }

  if (funnelData.mealSize) {
    parts.push(`Meal Size: ${formatMealSize(funnelData.mealSize)}`);
  }

  if (funnelData.dislikes && funnelData.dislikes.length > 0) {
    parts.push(`Dislikes: ${formatDislikes(funnelData.dislikes)}`);
  }

  return parts.length > 0 ? parts.join(', ') : '-';
};
